/* After10thQuiz.jsx — One self-contained group quiz + report
 * for the chosen Intermediate group (MPC | BiPC | MEC | CEC | HEC).
 *
 * One-question-at-a-time experience: progress indicator "Question X of 18",
 * single question card on screen, Next button advances. After Q18 the
 * client submits all answers → server scores → full honest report.
 *
 * Strict, end-to-end. Completely independent of After-Inter flow.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { cardToast } from '../lib/clarityCard';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2, ArrowRight, ArrowLeft, ChevronLeft, CheckCircle2, XCircle, Lightbulb,
  Sparkles, AlertTriangle, ShieldCheck,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { QuizOptions, QuizSidePanel, QuestionDots, KeyHint, byId } from '../components/clarity/QuizStage';

const API = process.env.REACT_APP_BACKEND_URL;

// Per-group palette (matches the chooser tiles so the journey feels cohesive)
const GROUP_PALETTE = {
  MPC:  { from: 'from-blue-50',    via: 'via-white', to: 'to-indigo-50',     accent: 'from-blue-500 to-indigo-500',     chipBg: 'bg-blue-100',    chipText: 'text-blue-800',    ring: 'ring-blue-200',
          grad: 'from-blue-500 to-indigo-500',    shadow: 'shadow-blue-200',    hover: 'hover:border-blue-300 hover:bg-blue-50/60',       keyHover: 'group-hover:border-blue-300 group-hover:text-blue-700',       dot: 'bg-blue-500' },
  BiPC: { from: 'from-emerald-50', via: 'via-white', to: 'to-green-50',      accent: 'from-emerald-500 to-green-600',   chipBg: 'bg-emerald-100', chipText: 'text-emerald-800', ring: 'ring-emerald-200',
          grad: 'from-emerald-500 to-green-600',  shadow: 'shadow-emerald-200', hover: 'hover:border-emerald-300 hover:bg-emerald-50/60', keyHover: 'group-hover:border-emerald-300 group-hover:text-emerald-700', dot: 'bg-emerald-500' },
  MEC:  { from: 'from-violet-50',  via: 'via-white', to: 'to-fuchsia-50',    accent: 'from-violet-500 to-fuchsia-500',  chipBg: 'bg-violet-100',  chipText: 'text-violet-800',  ring: 'ring-violet-200',
          grad: 'from-violet-500 to-fuchsia-500', shadow: 'shadow-violet-200',  hover: 'hover:border-violet-300 hover:bg-violet-50/60',   keyHover: 'group-hover:border-violet-300 group-hover:text-violet-700',   dot: 'bg-violet-500' },
  CEC:  { from: 'from-amber-50',   via: 'via-white', to: 'to-orange-50',     accent: 'from-amber-500 to-orange-500',    chipBg: 'bg-amber-100',   chipText: 'text-amber-900',   ring: 'ring-amber-200',
          grad: 'from-amber-500 to-orange-500',   shadow: 'shadow-amber-200',   hover: 'hover:border-amber-300 hover:bg-amber-50/60',     keyHover: 'group-hover:border-amber-300 group-hover:text-amber-700',     dot: 'bg-amber-500' },
  HEC:  { from: 'from-rose-50',    via: 'via-white', to: 'to-pink-50',       accent: 'from-rose-500 to-pink-500',       chipBg: 'bg-rose-100',    chipText: 'text-rose-800',    ring: 'ring-rose-200',
          grad: 'from-rose-500 to-pink-500',      shadow: 'shadow-rose-200',    hover: 'hover:border-rose-300 hover:bg-rose-50/60',       keyHover: 'group-hover:border-rose-300 group-hover:text-rose-700',       dot: 'bg-rose-500' },
};

const BAND_STYLE = {
  'Strong fit': { color: 'text-emerald-700', bg: 'bg-emerald-50',  ring: 'ring-emerald-200', Icon: ShieldCheck },
  'Medium fit': { color: 'text-amber-700',   bg: 'bg-amber-50',    ring: 'ring-amber-200',   Icon: Sparkles },
  'Low fit':    { color: 'text-rose-700',    bg: 'bg-rose-50',     ring: 'ring-rose-200',    Icon: AlertTriangle },
};

export default function After10thQuiz() {
  const { group } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quiz, setQuiz] = useState(null);
  const [meta, setMeta] = useState({ attempts_used: 0, attempts_remaining: 3, locked: false, latest_report: null, is_admin: false });
  const [answers, setAnswers] = useState({});  // { Q1: 'A', Q2: 'C', ... }
  const [stepIdx, setStepIdx] = useState(0);    // 0-based index across the 18 questions
  const [submitting, setSubmitting] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const advanceTimer = useRef(null);
  const [report, setReport] = useState(null);

  const groupUpper = (group || '').toUpperCase();
  const palette = GROUP_PALETTE[Object.keys(GROUP_PALETTE).find((k) => k.toUpperCase() === groupUpper)] || GROUP_PALETTE.MPC;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get(`${API}/api/after-10th/quiz/${groupUpper}`, { withCredentials: true });
        if (cancelled) return;
        setQuiz(data.quiz);
        setMeta({
          attempts_used: data.attempts_used,
          attempts_remaining: data.attempts_remaining,
          locked: data.locked,
          is_admin: !!data.is_admin,
          latest_report: data.latest_report?.report || data.latest_report || null,
        });
        if (data.locked && data.latest_report) {
          setReport(data.latest_report?.report || data.latest_report);
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          if (!cancelled) navigate('/signin', { replace: true });
          return;
        }
        if (!cancelled) setError(e?.response?.data?.detail || 'Could not load the quiz.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupUpper, navigate]);

  /* Flatten all 18 questions while preserving the section/area for each one. */
  const flatQuestions = useMemo(() => {
    if (!quiz) return [];
    return quiz.sections.flatMap((s, secIdx) =>
      s.questions.map((q) => ({ ...q, area: s.area, secIdx }))
    );
  }, [quiz]);
  const totalQs = flatQuestions.length;
  const current = flatQuestions[stepIdx] || null;
  const isLast = stepIdx === totalQs - 1;
  const displayOptions = useMemo(
    () => (current ? current.options.map((o) => ({ ...o, id: o.key })).sort(byId) : []),
    [current],
  );

  useEffect(() => () => { if (advanceTimer.current) clearTimeout(advanceTimer.current); }, []);

  const submitAll = async (finalAnswers) => {
    setSubmitting(true);
    setError('');
    try {
      const body = {
        answers: Object.entries(finalAnswers).map(([question_id, option_key]) => ({ question_id, option_key })),
      };
      const { data } = await axios.post(
        `${API}/api/after-10th/submit/${groupUpper}`,
        body,
        { withCredentials: true },
      );
      setReport(data.report);
      cardToast();
      setMeta((m) => ({ ...m, attempts_used: data.attempts_used, attempts_remaining: data.attempts_remaining, locked: data.attempts_remaining === 0 }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* Tap an answer → save it, show the tick, then auto-advance (or submit on the last one). */
  const pick = (opt) => {
    if (!current || advancing || submitting) return;
    const next = { ...answers, [current.id]: opt.key };
    setAnswers(next);
    setAdvancing(true);
    advanceTimer.current = setTimeout(() => {
      setAdvancing(false);
      if (isLast) { submitAll(next); return; }
      setStepIdx((i) => Math.min(i + 1, totalQs - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 550);
  };

  const goBack = () => {
    if (advanceTimer.current) { clearTimeout(advanceTimer.current); setAdvancing(false); }
    if (stepIdx > 0) {
      setStepIdx((i) => Math.max(0, i - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ── Loading / error guards ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF9F1]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600"/>
      </div>
    );
  }
  if (error && !quiz) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar/>
        <div className="pt-32 max-w-xl mx-auto px-4 text-center" data-testid="after10th-quiz-error">
          <p className="text-red-600 mb-4">{error}</p>
          <Link to="/career-clarity/after-10th" className="text-teal-700 underline">← Back to groups</Link>
        </div>
      </div>
    );
  }

  // ── REPORT VIEW ───────────────────────────────────────────────────────
  if (report) {
    const style = BAND_STYLE[report.band] || BAND_STYLE['Medium fit'];
    const BandIcon = style.Icon;
    const a = report.always_show || {};
    const q16 = report.q16 || {};
    return (
      <div className={`min-h-screen bg-gradient-to-br ${palette.from} ${palette.via} ${palette.to}`} data-testid="after10th-report">
        <Navbar/>
        <div className="pt-28 md:pt-32 pb-16 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <Link to="/career-clarity/after-10th" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-teal-700 mb-4" data-testid="after10th-report-back">
              <ChevronLeft size={16}/> Back to groups
            </Link>

            {/* Coloured band hero */}
            <div className={`relative rounded-3xl ${style.bg} ring-1 ${style.ring} p-6 md:p-8 overflow-hidden`} data-testid="after10th-band-card">
              <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full bg-gradient-to-br ${palette.accent} opacity-10`}/>
              <div className="flex items-center gap-3 mb-2 relative">
                <BandIcon className={`w-7 h-7 ${style.color}`}/>
                <p className={`text-[11px] font-extrabold uppercase tracking-widest ${style.color}`}>Your honest fit · {report.group_full_form}</p>
              </div>
              <h1 className={`text-3xl md:text-4xl font-extrabold ${style.color} relative`} data-testid="after10th-band-name">
                {report.band}
                <span className="text-base font-bold text-gray-500 ml-3 align-middle">· {report.score} / {report.max_score}</span>
              </h1>
              <p className="text-sm md:text-base text-gray-700 mt-3 leading-relaxed relative" data-testid="after10th-band-meaning">{report.band_meaning}</p>
              <p className="text-sm md:text-base text-gray-800 mt-3 leading-relaxed font-medium relative" data-testid="after10th-band-message">{report.by_band_message}</p>
              {report.strong_fit_gate_note && (
                <p className="text-sm text-gray-700 mt-3 leading-relaxed relative" data-testid="after10th-gate-note">{report.strong_fit_gate_note}</p>
              )}
              {report.borderline && (
                <div className="mt-3 relative" data-testid="after10th-borderline">
                  {report.borderline.other_message && (
                    <p className="text-sm text-gray-700 leading-relaxed"><span className="font-bold">{report.borderline.other_band}: </span>{report.borderline.other_message}</p>
                  )}
                  {report.borderline.note && <p className="text-sm text-gray-700 mt-2 leading-relaxed">{report.borderline.note}</p>}
                </div>
              )}
              {report.consistency_note && (
                <p className="text-sm text-gray-700 mt-3 leading-relaxed relative" data-testid="after10th-consistency-note">{report.consistency_note}</p>
              )}
            </div>

            {/* Area sub-scores */}
            {report.sub_scores && Object.keys(report.sub_scores).length > 0 && (
              <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-5 md:p-6" data-testid="after10th-subscores">
                <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-3">Your area scores</p>
                {Object.entries(report.sub_scores).map(([area, s]) => (
                  <div key={area} className="flex items-center justify-between gap-3 py-1.5">
                    <p className="text-sm text-gray-800 leading-relaxed">{area}</p>
                    <p className="text-sm font-bold text-gray-900 whitespace-nowrap">{s.score} / {s.max}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Q16 correctness */}
            {(q16.is_correct === true || q16.is_correct === false) && (
              <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-5 md:p-6" data-testid="after10th-q16-card">
                <div className="flex items-center gap-2 mb-2">
                  {q16.is_correct ? (
                    <><CheckCircle2 className="w-5 h-5 text-emerald-600"/><span className="text-sm font-bold text-emerald-700">Knowledge check — correct!</span></>
                  ) : (
                    <><XCircle className="w-5 h-5 text-rose-600"/><span className="text-sm font-bold text-rose-700">Knowledge check — not quite</span></>
                  )}
                </div>
                {q16.correct_option && !q16.is_correct && (
                  <p className="text-sm text-gray-700">The correct answer was option <span className="font-bold">{q16.correct_option}</span>.</p>
                )}
                {q16.explanation && <p className="text-sm text-gray-700 mt-2 leading-relaxed">{q16.explanation}</p>}
              </div>
            )}

            {/* Always-show factual block */}
            <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-5 md:p-7" data-testid="after10th-always-show">
              <h2 className="text-xl font-extrabold text-gray-900 mb-4">{a.group}</h2>
              {a.what_you_study && (<div className="mb-4"><p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">What you study</p><p className="text-sm text-gray-800 mt-1 leading-relaxed">{a.what_you_study}</p></div>)}
              {a.where_it_leads && (<div className="mb-4"><p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">Where it leads</p><p className="text-sm text-gray-800 mt-1 leading-relaxed">{a.where_it_leads}</p></div>)}
              {a.does_not_lead_to && (<div className="mb-4"><p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">Does not lead to</p><p className="text-sm text-gray-800 mt-1 leading-relaxed">{a.does_not_lead_to}</p></div>)}
              {a.ai_angle && (<div className="mb-4"><p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">AI angle</p><p className="text-sm text-gray-800 mt-1 leading-relaxed">{a.ai_angle}</p></div>)}
              {a.honest_note && (
                <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-100">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-700 mt-0.5 shrink-0"/>
                    <p className="text-sm text-amber-900 leading-relaxed"><span className="font-bold">Honest note: </span>{a.honest_note}</p>
                  </div>
                </div>
              )}
            </div>

            {/* AI awareness */}
            {report.ai_awareness_note && (
              <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-5 md:p-6" data-testid="after10th-ai-note">
                <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-2">A note about AI</p>
                <p className="text-sm text-gray-800 leading-relaxed">{report.ai_awareness_note}</p>
              </div>
            )}

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/career-clarity/after-10th"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gray-900 text-white text-sm font-bold hover:bg-gray-800 transition"
                data-testid="after10th-try-another"
              >
                Try another group <ArrowRight size={16}/>
              </Link>
              <p className="text-xs text-gray-500 self-center" data-testid="after10th-attempts-info">
                {meta.attempts_remaining > 0
                  ? `You can retake this ${meta.attempts_remaining} more time${meta.attempts_remaining === 1 ? '' : 's'}.`
                  : `You've used all attempts on ${quiz.group}.`}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── LOCKED VIEW (cap reached, no cached report) ─────────────────────
  if (meta.locked) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${palette.from} ${palette.via} ${palette.to}`}>
        <Navbar/>
        <div className="pt-32 max-w-2xl mx-auto px-4 text-center" data-testid="after10th-locked">
          <ShieldCheck className="w-10 h-10 text-amber-600 mx-auto mb-3"/>
          <h2 className="text-2xl font-extrabold text-gray-900">You&apos;ve used all attempts on {quiz.group}.</h2>
          <p className="text-gray-600 mt-3">Try another group to compare your fit.</p>
          <Link to="/career-clarity/after-10th" className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-full bg-gray-900 text-white text-sm font-bold hover:bg-gray-800">
            Try another group <ArrowRight size={16}/>
          </Link>
        </div>
      </div>
    );
  }

  // ── ONE-QUESTION-AT-A-TIME QUIZ VIEW ───────────────────────────────────
  if (!current) return null;
  const selected = answers[current.id];
  const answeredIds = new Set(Object.keys(answers));

  return (
    <div className={`min-h-screen bg-gradient-to-br ${palette.from} ${palette.via} ${palette.to}`} data-testid="after10th-quiz">
      <Navbar/>

      <div className="pt-28 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link to="/career-clarity/after-10th" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-teal-700" data-testid="after10th-quiz-back">
              <ChevronLeft size={16}/> Back to groups
            </Link>
            <p className="text-xs text-gray-500" data-testid="after10th-attempts-left">
              {meta.is_admin
                ? <span className="font-semibold text-emerald-700">Admin · unlimited attempts</span>
                : <><span className="font-semibold">{meta.attempts_remaining}</span> of {meta.attempts_remaining + meta.attempts_used} attempts left</>
              }
            </p>
          </div>

          <p className={`text-[11px] font-extrabold uppercase tracking-[0.18em] mt-5 ${palette.chipText}`}>
            After 10th · {quiz.group} fit quiz
          </p>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-1.5 leading-tight" data-testid="after10th-quiz-title">
            {quiz.group} — {quiz.group_full_form}
          </h1>
          {quiz.student_instruction && stepIdx === 0 && (
            <p className="text-xs text-gray-500 mt-2 leading-relaxed max-w-2xl" data-testid="after10th-student-instruction">{quiz.student_instruction}</p>
          )}

          <div className="mt-6 grid md:grid-cols-[230px_1fr] gap-5 items-start">
            <aside className="md:sticky md:top-28 space-y-3">
              <QuizSidePanel
                grad={palette.grad}
                eyebrow={`Area ${current.secIdx + 1} of ${quiz.sections.length}`}
                title={current.area}
                index={stepIdx}
                total={totalQs}
                testId="after10th-step-indicator"
              />
              <QuestionDots ids={flatQuestions.map((q) => q.id)} answered={answeredIds} currentId={current.id} dotClass={palette.dot} />
            </aside>

            <main>
              <div data-testid={`after10th-step-${current.id}`}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current.id}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="relative bg-white rounded-[1.75rem] border border-gray-100 shadow-xl shadow-gray-200/50 overflow-hidden"
                  >
                    <div className={`h-1.5 bg-gradient-to-r ${palette.accent}`}/>
                    <div className="p-5 md:p-8">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full ${palette.chipBg} ${palette.chipText} text-[10px] font-extrabold uppercase tracking-widest`}>
                        Question {stepIdx + 1}
                      </span>
                      <p className="text-xl md:text-[1.65rem] font-extrabold text-gray-900 mt-3 mb-6 leading-snug" data-testid={`after10th-q-${current.id}-text`}>
                        {current.question}
                      </p>
                      <QuizOptions
                        options={displayOptions}
                        selectedId={selected}
                        onSelect={pick}
                        disabled={advancing || submitting}
                        accent={palette}
                        testIdPrefix={`after10th-opt-${current.id}`}
                      />
                      {submitting ? (
                        <p className={`mt-5 text-sm font-bold ${palette.chipText} flex items-center justify-center gap-2`} data-testid="after10th-scoring">
                          <Loader2 size={15} className="animate-spin"/> Scoring your honest fit…
                        </p>
                      ) : (
                        <KeyHint last={displayOptions[displayOptions.length - 1]?.id} />
                      )}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {error && <p className="text-sm text-red-600 mt-4" data-testid="after10th-submit-error">{error}</p>}
              <div className="mt-4 flex items-center justify-between gap-3" data-testid="after10th-nav">
                <button
                  type="button"
                  onClick={goBack}
                  disabled={stepIdx === 0 || submitting}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl border-2 bg-white text-sm font-semibold transition ${stepIdx === 0 ? 'text-gray-300 border-gray-100 cursor-not-allowed' : 'text-gray-700 border-gray-200 hover:border-gray-300'}`}
                  data-testid="after10th-prev-btn"
                >
                  <ArrowLeft size={14}/> Back
                </button>
                <p className="text-xs text-gray-500" data-testid="after10th-answered-count">
                  {answeredIds.size} / {totalQs} answered
                </p>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
